if (!window.customElements.get('store-pills')) {
  class StorePills extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.hasInitialized = false;
      this.isAtStart = true;
      this.isAtEnd = false;
      this.gsap = null;
      this.loop = false;
      this.handleResize = null;
    }
  
    connectedCallback() {
      const sliderContainer = this.querySelector('.keen-slider');
      if (!sliderContainer || sliderContainer.children.length < 2) return;
  
      // Add smooth transitions to the arrow
      const arrow = this.querySelector('.keen-slider__arrow--cursor');
      if (arrow) {
        arrow.style.transition = 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out';
      }
  
      // Wait for both KeenSlider and GSAP to be ready
      whenReady('KeenSlider', () => {
        whenReady('motion', (motion) => {
          this.motion = motion;
          try {
            this.initializeSlider(sliderContainer);
          } catch (error) {
            console.error('Failed to initialize KeenSlider:', error);
          }
        });
      });
    }
  
    initializeSlider(container) {
      const arrow = this.querySelector('.keen-slider__arrow--cursor');
      let isInNavigationZone = false;
      
      // Check if content overflows and needs scrolling
      const checkOverflow = () => {
        const track = container.querySelector('.keen-slider');
        if (!track) return false;
        
        const containerWidth = container.offsetWidth;
        const trackWidth = track.scrollWidth;
        
        return trackWidth > containerWidth;
      };
  
      // Wheel Controls function (from Keen Slider example)
      const WheelControls = (slider) => {
        let touchTimeout;
        let position;
        let wheelActive;
        let isHorizontalScroll = false;
  
        function dispatch(e, name) {
          position.x -= e.deltaX;
          position.y -= e.deltaY;
          slider.container.dispatchEvent(
            new CustomEvent(name, {
              detail: {
                x: position.x,
                y: position.y,
              },
            })
          );
        }
  
        function wheelStart(e) {
          position = {
            x: e.pageX,
            y: e.pageY,
          };
          dispatch(e, "ksDragStart");
        }
  
        function wheel(e) {
          dispatch(e, "ksDrag");
        }
  
        function wheelEnd(e) {
          dispatch(e, "ksDragEnd");
        }
  
        function eventWheel(e) {
          // Check if this is primarily a horizontal scroll
          const isHorizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY);
          
          // If it's a horizontal scroll or we're already in an active horizontal scroll
          if (isHorizontal || wheelActive) {
            e.preventDefault();
            e.stopPropagation();
            
            if (!wheelActive) {
              wheelStart(e);
              wheelActive = true;
              isHorizontalScroll = true;
            }
            wheel(e);
            clearTimeout(touchTimeout);
            touchTimeout = setTimeout(() => {
              wheelActive = false;
              isHorizontalScroll = false;
              wheelEnd(e);
            }, 50);
          }
        }
  
        slider.on("created", () => {
          const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
          if (!isSafari) {
              slider.container.addEventListener("wheel", eventWheel, {
                  passive: false,
              });
          }
        });
      };
  
      // Progress nav logic
      const progressNav = this.querySelector('.keen-slider__progress-nav');
      const progressBar = progressNav ? progressNav.querySelector('.keen-slider__progress-bar') : null;
      const slides = this.querySelectorAll('.keen-slider__slide');
      const slidesInView = 2;
  
      // Add click handler to progress nav
      if (progressNav) {
        progressNav.addEventListener('click', (e) => {
          if (!this.slider) return;
  
          // Get the click position within the nav
          const rect = progressNav.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          const clickPercentage = clickX / rect.width;
  
          let clampedIndex;
          if (this.loop) {
            // In loop mode, use the number of slides
            const slideCount = this.slider.track.details.slides.length;
            const targetSlideIndex = Math.floor(clickPercentage * slideCount);
            clampedIndex = Math.min(targetSlideIndex, slideCount - 1);
          } else {
            // Non-looping: use maxIdx as before
            const maxIdx = this.slider.track.details.maxIdx;
            const targetSlideIndex = Math.floor(clickPercentage * maxIdx);
            clampedIndex = Math.min(targetSlideIndex, maxIdx);
          }
  
          // console.log('Moving to slide:', clampedIndex);
  
          // Move to the target slide
          this.slider.moveToIdx(clampedIndex);
        });
      }
  
      // Update progress bar position
      const updateProgressBar = (relIdx) => {
        if (!progressBar) return;
  
        const maxIdx = this.slider ? this.slider.track.details.maxIdx : slides.length - slidesInView;
        const isAtEnd = relIdx >= maxIdx;
        const isAtStart = relIdx === 0;
  
        // Get the progress nav container to calculate available width
        const progressNavWidth = progressNav.offsetWidth + 8;
        const barWidth = window.innerWidth > 991 ? 235 : 85;
        const maxTranslateX = progressNavWidth - barWidth; // Maximum translation
  
        if (this.loop && this.slider) {
          const progress = this.slider.track.details.progress;
          const translateX = progress * maxTranslateX;
          if (this.gsap) {
            this.gsap.set(progressBar, { 
              x: translateX,
              transition: 'none'
            });
          } else {
            progressBar.style.transform = `translateX(${translateX}px)`;
            progressBar.style.transition = 'none';
            progressBar.style.opacity = '1';
          }
          return;
        }
  
        // Non-looping: original calculation
        const progressPercentage = isAtEnd ? 100 : (relIdx / maxIdx) * 100;
        const translateX = (progressPercentage / 100) * maxTranslateX;
        if (this.gsap) {
          this.gsap.to(progressBar, { 
            x: translateX, 
            opacity: 1,
            duration: 0.3, 
            ease: "expo.out" 
          });
        } else {
          progressBar.style.transform = `translateX(${translateX}px)`;
          progressBar.style.transition = 'none';
          progressBar.style.opacity = '1';
        }
      };
  
      const sliderConfig = {
        loop: this.loop,
        rubberband: false,
        slides: {
          perView: "auto",
          origin: "auto",
          spacing: 16
        },
        defaultAnimation: {
          duration: 1000,
          easing: (t) => {
            return 1 - Math.pow(1 - t, 7);
          }
        },  
        dragSpeed: 1.2,
        dragAngle: 45,
        mode: "free-snap",
        created(s) {
          // Check if content overflows and center if not
          setTimeout(() => {
            const track = s.container;
            if (track) {
              const containerWidth = s.container.offsetWidth;
              const trackWidth = track.scrollWidth;
              
              if (trackWidth <= containerWidth) {
                // Content fits, center it
                const offset = (containerWidth - trackWidth) / 2;
                track.style.transform = `translateX(${offset}px)`;
                track.style.justifyContent = 'center';
              } else {
                // Content overflows, enable scrolling
                track.style.justifyContent = 'flex-start';
              }
            }
          }, 100);
          
          // Prevent vertical scrolling during touch interactions
          s.container.addEventListener('touchstart', (e) => {
            if (e.touches.length === 1) {
              const touch = e.touches[0];
              s.container._touchStartY = touch.clientY;
              s.container._touchStartX = touch.clientX;
            }
          }, { passive: true });
          
          s.container.addEventListener('touchmove', (e) => {
            if (e.touches.length === 1 && s.container._touchStartY !== undefined) {
              const touch = e.touches[0];
              const deltaY = Math.abs(touch.clientY - s.container._touchStartY);
              const deltaX = Math.abs(touch.clientX - s.container._touchStartX);
              
              // If horizontal movement is greater than vertical, prevent vertical scroll
              if (deltaX > deltaY && deltaX > 10) {
                e.preventDefault();
              }
            }
          }, { passive: false });
          
          s.container.addEventListener('touchend', (e) => {
            delete s.container._touchStartY;
            delete s.container._touchStartX;
          }, { passive: true });
        },
        detailsChanged: (s) => {
          this.isAtStart = s.track.details.rel === 0;
          this.isAtEnd = s.track.details.rel === s.track.details.maxIdx;
          this.handleSlideChange(s, container);
          updateProgressBar(s.track.details.rel);
        }
      };
  
      this.slider = new window.KeenSlider(container, sliderConfig, [WheelControls]);
  
      // Initialize progress nav on first load
      updateProgressBar(window.innerWidth > 991 ? 1 : 0);
  
      // Add resize handler to recalculate centering
      this.handleResize = () => {
        setTimeout(() => {
          const track = container;
          if (track && this.slider) {
            const containerWidth = container.offsetWidth;
            const trackWidth = track.scrollWidth;
            
            if (trackWidth <= containerWidth) {
              // Content fits, center it
              const offset = (containerWidth - trackWidth) / 2;
              track.style.transform = `translateX(${offset}px)`;
              track.style.justifyContent = 'center';
            } else {
              // Content overflows, enable scrolling
              track.style.justifyContent = 'flex-start';
              // Reset transform to let KeenSlider handle positioning
              track.style.transform = '';
            }
          }
        }, 100);
      };
  
      window.addEventListener('resize', this.handleResize);
  
      if (!this.hasInitialized) {
        this.hasInitialized = true;
  
        setTimeout(() => {
          container.classList.add("keen-slider--initialized")
        }, 300);
      }
    }
  
    handleSlideChange(slider, container) {
      slider.slides.forEach((element, idx) => {
        const portion = slider.track.details.slides[idx].portion;
        const isActive = portion === 1;
        element.classList.toggle('keen-slider__slide--active', isActive);
      });
    }
  
    // Public methods for external control
    next() {
      if (this.slider) {
        this.slider.next();
      }
    }
  
    prev() {
      if (this.slider) {
        this.slider.prev();
      }
    }
  
    moveToIdx(index) {
      if (this.slider) {
        this.slider.moveToIdx(index);
      }
    }
  
    // Get current slide info
    getCurrentSlide() {
      if (this.slider) {
        return {
          current: this.slider.track.details.rel,
          total: this.slider.track.details.slides.length,
          isAtStart: this.isAtStart,
          isAtEnd: this.isAtEnd
        };
      }
      return null;
    }
  
    disconnectedCallback() {
      if (this.slider) {
        this.slider.destroy();
        this.slider = null;
      }
      
      // Remove resize handler
      window.removeEventListener('resize', this.handleResize);
      
      this.hasInitialized = false;
    }
  }
  customElements.define('store-pills', StorePills);
}

if (!window.customElements.get('store-modal')) {
  class StoreModal extends HTMLElement {
    constructor() {
      super();
      this.isInitialized = false;
      this.hasMoved = false;
      this.originalParent = null;
      this.originalNextSibling = null;
      this.isMoving = false; // Add flag to prevent recursive calls
    }
  
    connectedCallback() {
      if (this.isInitialized) return;
      
      // Move the web component outside of ScrollWrapper first
      this.moveOutsideScrollWrapper();
  
    }
  
    moveOutsideScrollWrapper() {
      // Prevent recursive calls
      if (this.hasMoved || this.isMoving) return;
      
      try {
        this.isMoving = true; // Set flag to prevent recursive calls
        
        const scrollWrapper = document.getElementById('ScrollWrapper');
        const body = document.body;
        
        // Additional safety checks
        if (!scrollWrapper || !body) {
          console.warn('StorePillsSlideout: Required elements not found for moving');
          this.isMoving = false;
          return;
        }
        
        // Check if we're already outside ScrollWrapper
        if (!scrollWrapper.contains(this)) {
          // console.log('StorePillsSlideout: Already outside ScrollWrapper');
          this.hasMoved = true;
          this.isMoving = false;
          return;
        }
        
        // Store original position for potential restoration
        this.originalParent = this.parentNode;
        this.originalNextSibling = this.nextSibling;
        
        // Move just the web component to the end of body (outside ScrollWrapper)
        body.appendChild(this);
        this.hasMoved = true;
        
        // Ensure proper positioning after move
        this.ensureProperPositioning();
        
        // console.log('StorePillsSlideout component moved outside ScrollWrapper');
        
      } catch (error) {
        console.error('Failed to move StorePillsSlideout component outside ScrollWrapper:', error);
      } finally {
        this.isMoving = false; // Always reset the flag
      }
    }
  
    ensureProperPositioning() {
      // Ensure the web component itself has proper positioning
      this.style.position = 'fixed';
      this.style.top = '0';
      this.style.left = '-99999999px';
      this.style.width = '100vw';
      this.style.height = '100vh';
      this.style.zIndex = '9999999';
      this.style.isolation = 'isolate';
      
      // Ensure it's not affected by parent transforms or positioning
      this.style.transform = 'none';
      this.style.transformOrigin = 'initial';
    }
  
    // Method to restore original position (if needed)
    restoreOriginalPosition() {
      if (this.hasMoved && this.originalParent && this.originalNextSibling) {
        try {
          if (this.originalNextSibling) {
            this.originalParent.insertBefore(this, this.originalNextSibling);
          } else {
            this.originalParent.appendChild(this);
          }
          this.hasMoved = false;
          // console.log('StorePillsSlideout component restored to original position');
        } catch (error) {
          console.error('Failed to restore StorePillsSlideout component to original position:', error);
        }
      }
    }
  
    // Cleanup method
    disconnectedCallback() {
      this.isInitialized = false;
      this.hasMoved = false;
      this.isMoving = false;
      this.originalParent = null;
      this.originalNextSibling = null;
    }
  }
  // Register the web components
  customElements.define('store-modal', StoreModal);
}
